import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { Truck, Loader2 } from "lucide-react";
import { formatNumber } from "@/lib/utils";

import { toVnDateString } from "@apv-erp/status";
interface OutsourceDispatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  outsourcePO: any | null;
  onSuccess?: () => void;
}

export function OutsourceDispatchModal({
  isOpen,
  onClose,
  outsourcePO,
  onSuccess,
}: OutsourceDispatchModalProps) {
  const [qtyDispatched, setQtyDispatched] = useState<number>(0);
  const [dispatchDate, setDispatchDate] = useState<string>(
    toVnDateString()
  );
  const [notes, setNotes] = useState<string>("");
  const [createInventoryIssue, setCreateInventoryIssue] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (outsourcePO) {
      const totalPlanned =
        (outsourcePO.qtyOrdered || 0) + (outsourcePO.allowanceQty || 0);
      const alreadySent = outsourcePO.qtySentTotal || 0;
      const remainingToSend = Math.max(0, totalPlanned - alreadySent);
      setQtyDispatched(remainingToSend > 0 ? remainingToSend : outsourcePO.qtyOrdered || 100);
      setDispatchDate(toVnDateString());
      setNotes("");
      setCreateInventoryIssue(true);
    }
  }, [outsourcePO]);

  if (!outsourcePO) return null;

  const alreadySent = outsourcePO.qtySentTotal || 0;

  const handleConfirm = async () => {
    if (!qtyDispatched || qtyDispatched <= 0) {
      toast.error("Vui lòng nhập số lượng BTP xuất gửi hợp lệ (> 0)");
      return;
    }

    setIsSubmitting(true);
    try {
      await trpcClient.outsourcePos.dispatch.mutate({
        id: outsourcePO.id,
        qtyDispatched: Number(qtyDispatched),
        dispatchDate,
        notes: notes || undefined,
        createInventoryIssue,
      });

      toast.success(
        `Đã xuất gửi ${formatNumber(qtyDispatched)} BTP cho đơn gia công ${outsourcePO.poId}!`
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xuất gửi BTP gia công");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-indigo-600" />
            Xuất Giao Bán Thành Phẩm Gia Công
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          {/* Summary Box */}
          <div className="rounded-lg bg-slate-50 p-3 border border-slate-200 space-y-1.5">
            <div className="flex justify-between">
              <span className="text-slate-500 text-xs">Mã đơn GC:</span>
              <span className="font-mono font-medium text-indigo-600">
                {outsourcePO.poId}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 text-xs">Đơn vị gia công:</span>
              <span className="font-medium text-slate-700">
                {outsourcePO.vendor?.name || outsourcePO.vendor?.code || "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 text-xs">Sản phẩm / Công đoạn:</span>
              <span className="font-medium text-slate-700 text-right max-w-[220px] break-words whitespace-normal">
                {outsourcePO.productName || outsourcePO.productSpecs || "BTP"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200 text-xs">
              <div>
                <span className="text-slate-500">Đặt hàng: </span>
                <span className="font-semibold">{formatNumber(outsourcePO.qtyOrdered)}</span>
              </div>
              <div className="text-right">
                <span className="text-slate-500">Đã gửi: </span>
                <span className="font-semibold text-amber-600">{formatNumber(alreadySent)}</span>
              </div>
            </div>
          </div>

          {/* Form Inputs */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">
                Số lượng BTP xuất đi đợt này (pcs) <span className="text-rose-500">*</span>
              </Label>
              <Input
                type="number"
                min={1}
                value={qtyDispatched || ""}
                onChange={(e) => setQtyDispatched(Number(e.target.value))}
                className="mt-1 font-semibold"
                placeholder="Nhập số lượng xuất đi..."
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">
                Ngày xuất giao
              </Label>
              <Input
                type="date"
                value={dispatchDate}
                onChange={(e) => setDispatchDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">
                Ghi chú / Người giao nhận / Số xe
              </Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="VD: Giao bác Nam xe tải 29C-123.45..."
                className="mt-1 text-xs"
              />
            </div>

            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="createInventoryIssue"
                checked={createInventoryIssue}
                onCheckedChange={(checked) => setCreateInventoryIssue(!!checked)}
              />
              <Label
                htmlFor="createInventoryIssue"
                className="text-xs text-slate-600 cursor-pointer"
              >
                Tự động tạo Phiếu Xuất Kho BTP (loại ISSUE từ KHO-WIP)
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Đang xử lý...
              </>
            ) : (
              <>
                <Truck className="mr-1.5 h-4 w-4" />
                Xác Nhận Xuất Gửi
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
